import { Address, Coupon, ICreditCard, QuotationResponse, Recipe } from '@/types';
import type { PaymentFailureVariant } from '@/lib/checkout/payment-failure';
import type { CheckoutShippingStatus } from '@/lib/checkout/checkout-blockers';
import { CHECKOUT } from '@/lib/constants';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

interface DeliveryTime {
  id: string;
  title: string;
  description: string;
}

interface VerifiedResponse {
  total_tax: number;
  shipping_charge: number;
  unavailable_products: any[];
  wallet_amount: number;
  wallet_currency: number;
}

interface CheckoutState {
  billing_address: Address | null;
  shipping_address: Address | null;
  payment_gateway?: number;
  payment_sub_gateway?: string;
  delivery_time: DeliveryTime | null | string | number | QuotationResponse; // TODO: review
  customer_contact: string;
  customer_name: string | null;
  verified_response: VerifiedResponse | null;
  coupon: Coupon | null;
  payable_amount: number;
  use_wallet: boolean;
  payment_credit_card?: ICreditCard;
  wallet_usage: number;
  note?: string;
  order_requests?: {
    recipes?: Recipe[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export const defaultCheckout: CheckoutState = {
  billing_address: null,
  shipping_address: null,
  delivery_time: null,
  payment_sub_gateway: '',
  customer_contact: '',
  customer_name: '',
  verified_response: null,
  coupon: null,
  note: '',
  payable_amount: 0,
  use_wallet: false,
  wallet_usage: 0,
  order_requests: {
    recipes: [],
  },
};

export const checkoutAtom = atomWithStorage(CHECKOUT, defaultCheckout);
export const clearCheckoutAtom = atom(null, (_get, set, _data) => {
  return set(checkoutAtom, defaultCheckout);
});
export const billingAddressAtom = atom(
  (get) => get(checkoutAtom).billing_address,
  (get, set, data: Address) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, billing_address: data });
  },
);
export const shippingAddressAtom = atom(
  (get) => get(checkoutAtom).shipping_address,
  (get, set, data: Address) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, shipping_address: data });
  },
);
export const deliveryTimeAtom = atom<QuotationResponse>();
export const paymentGatewayAtom = atom(
  (get) => get(checkoutAtom).payment_gateway,
  (get, set, data: number) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, payment_gateway: data });
  },
);

export const creditCardAtom = atom(
  (get) => get(checkoutAtom).payment_credit_card,
  (get, set, data: ICreditCard) => {
    const prev = get(checkoutAtom);
    set(checkoutAtom, { ...prev, payment_credit_card: data });
  },
);

export const installmentAtom = atom<number>(1);

export const paymentSubGatewayAtom = atom(
  (get) => get(checkoutAtom).payment_sub_gateway,
  (get, set, data: string) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, payment_sub_gateway: data });
  },
);

export const verifiedTokenAtom = atom(
  (get) => get(checkoutAtom).token,
  (get, set, data: string) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, token: data });
  },
);
export const customerContactAtom = atom(
  (get) => get(checkoutAtom).customer_contact,
  (get, set, data: string) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, customer_contact: data });
  },
);

export const userCpfAtom = atom('');

/** Collected on checkout when `userNeedsBasicInfo`; not persisted to localStorage. */
export const checkoutCustomerFirstNameAtom = atom('');
export const checkoutCustomerLastNameAtom = atom('');

export const guestNameAtom = atom(
  (get) => get(checkoutAtom).customer_name,
  (get, set, data: string) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, customer_name: data });
  },
);
export const orderNoteAtom = atom(
  (get) => get(checkoutAtom).note,
  (get, set, data: string) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, note: data });
  },
);
export const verifiedResponseAtom = atom(
  (get) => get(checkoutAtom).verified_response,
  (get, set, data: VerifiedResponse | null) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, verified_response: data });
  },
);
export const couponAtom = atom(
  (get) => get(checkoutAtom).coupon,
  (get, set, data: Coupon | null) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, coupon: data });
  },
);
export const discountAtom = atom((get) => get(checkoutAtom).coupon?.amount);

export const walletAtom = atom(
  (get) => get(checkoutAtom).use_wallet,
  (get, set, data: boolean) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, use_wallet: data });
  },
);

export const walletUsageAtom = atom(
  (get) => get(checkoutAtom).wallet_usage,
  (get, set, data: number) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, wallet_usage: data });
  },
);

export const payableAmountAtom = atom(
  (get) => get(checkoutAtom).payable_amount,
  (get, set, data: number) => {
    const prev = get(checkoutAtom);
    return set(checkoutAtom, { ...prev, payable_amount: data });
  },
);

export const savedRecipesAtom = atom(
  (get) => get(checkoutAtom).order_requests?.recipes || [],
  (get, set, data: Recipe[]) => {
    const prev = get(checkoutAtom);
    const updatedOrderRequests = {
      ...prev.order_requests,
      recipes: data,
    };
    return set(checkoutAtom, { ...prev, order_requests: updatedOrderRequests });
  },
);

/** Set to true when order API returns INSUFFICIENT_STOCK; checkout page shows stock modal. */
export const insufficientStockFromOrderTriggerAtom = atom(false);

/** Payment gateway failure; checkout page shows the recovery modal. */
export const paymentFailureModalAtom = atom<PaymentFailureVariant | null>(null);

/** Freight quotation state, so the order button can name a missing delivery step. */
export const checkoutShippingStatusAtom = atom<CheckoutShippingStatus>('idle');
